// App.js - Vollständig mit Analytics & Error Boundary
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom'
import { useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { HelmetProvider } from 'react-helmet-async'
import { useAuthState } from './hooks/useAuthState'
import { AuthProvider } from './contexts/AuthContext'
import { CompanyProvider, useCompany } from './contexts/CompanyContext'
import SearchPage from './pages/SearchPage'
import PersonalizationPage from './pages/PersonalizationPage'
import ProfilePage from './pages/ProfilePage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import LandingPage from './pages/LandingPage'
import LoadingSpinner from './components/LoadingSpinner'
import ProtectedRoute from './components/ProtectedRoute'
import Navigation from './components/Navigation'
import DynamicMetaTags from './components/DynamicMetaTags'
import ContributeKnowledgePage from './pages/ContributeKnowledgePage'
import AdminModerationPage from './pages/AdminModerationPage'
import UploadPage from './pages/UploadPage'
import KnowledgeBrowsePage from './pages/KnowledgeBrowsePage'
import ErrorBoundary from './components/ErrorBoundary'
import analyticsService from './services/analyticsService'
import AdminDashboard from './pages/AdminDashboard'
import DeviceManagerPage from './pages/DeviceManagerPage'
import InviteCodeManager from './pages/InviteCodeManager'
import UserManagementPage from './pages/UserManagementPage'
import SuperAdminCompaniesPage from './pages/SuperAdminCompaniesPage'
import ContactRequestsPage from './pages/ContactRequestsPage'
import SuperAdminConversationsPage from './pages/SuperAdminConversationsPage'
import MaintenancePage from './pages/MaintenancePage'
import TutorialsPage from './pages/TutorialsPage'
import TutorialDetail from './pages/Tutorialdetail'

const MAINTENANCE_MODE = false
const ALLOWED_LANDING_PAGE_DOMAIN = 'engineersknowledgebase.com'
import {
  useErrorTracking,
  useEngagementTracking,
  useNetworkMonitoring
} from './hooks/useAnalytics'
import './App.css'

// Prüft, ob die aktuelle Domain die LandingPage anzeigen darf
function isLandingPageAllowed () {
  const hostname = window.location.hostname
  // Erlaubt auch localhost für Entwicklung
  return hostname === ALLOWED_LANDING_PAGE_DOMAIN || hostname === 'localhost' || hostname === '127.0.0.1'
}

function CompanyInactiveBanner () {
  const { isCompanyActive, companyName, loading } = useCompany()

  if (loading || isCompanyActive) {
    return null
  }

  return (
    <div className='company-inactive-banner'>
      <div className='banner-content'>
        <span className='banner-icon'>⚠️</span>
        <div className='banner-text'>
          <strong>Company deaktiviert</strong>
          <p>
            {companyName ? `"${companyName}"` : 'Ihre Company'} ist derzeit deaktiviert.
            Bitte kontaktieren Sie den Support für weitere Informationen.
          </p>
        </div>
      </div>
    </div>
  )
}

function AppContent () {
  const { user, loading, userRole, hasModeratorAccess, hasAdminAccess } =
    useAuthState()

  if (MAINTENANCE_MODE) {
    return <MaintenancePage />
  }

  // Global Analytics Hooks
  useErrorTracking()
  useEngagementTracking()
  useNetworkMonitoring()

  // Track app initialization
  useEffect(() => {
    analyticsService.startSession()

    // Track app version/environment
    analyticsService.logEvent('app_initialized', {
      environment: process.env.NODE_ENV,
      user_agent: navigator.userAgent,
      screen_size: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`
    })

    // Track performance metrics
    if (window.performance) {
      const perfData = performance.getEntriesByType('navigation')[0]
      if (perfData) {
        analyticsService.logEvent('app_performance', {
          load_time: perfData.loadEventEnd - perfData.fetchStart,
          dom_content_loaded:
            perfData.domContentLoadedEventEnd - perfData.fetchStart,
          dns_time: perfData.domainLookupEnd - perfData.domainLookupStart,
          connection_time: perfData.connectEnd - perfData.connectStart
        })
      }
    }
  }, [])

  // Track authentication state changes
  useEffect(() => {
    if (user) {
      analyticsService.logEvent('user_authenticated', {
        user_id: user.uid,
        user_role: user.userData?.role || 'user',
        company: user.userData?.company || 'unknown'
      })
    } else if (!loading) {
      analyticsService.logEvent('user_unauthenticated')
    }
  }, [user, loading])

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className='app-container'>
      {/* Global Meta Tags - wird basierend auf Domain dynamisch gesetzt */}
      <DynamicMetaTags />
      
      {user && <Navigation />}
      {user && <CompanyInactiveBanner />}

      <Routes>
        <Route
          path='/'
          element={
            user ? (
              <Navigate to='/search' replace />
            ) : isLandingPageAllowed() ? (
              <LandingPage />
            ) : (
              <Navigate to='/login' replace />
            )
          }
        />

        <Route
          path='/login'
          element={user ? <Navigate to='/search' replace /> : <LoginPage />}
        />

        <Route
          path='/register'
          element={user ? <Navigate to='/search' replace /> : <RegisterPage />}
        />

        <Route
          path='/search'
          element={
            <ProtectedRoute>
              <SearchPage />
            </ProtectedRoute>
          }
        />

        {/* Moderation Route (accessible by moderator, admin, and superadmin) */}
        {hasModeratorAccess && (
          <Route
            path='/admin/moderation'
            element={
              <ProtectedRoute>
                <AdminModerationPage />
              </ProtectedRoute>
            }
          />
        )}

        {/* Admin Routes (accessible by admin and superadmin only) */}
        {hasAdminAccess && (
          <>
            <Route
              path='/upload'
              element={
                <ProtectedRoute>
                  <UploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/devices'
              element={
                <ProtectedRoute>
                  <DeviceManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/invite-codes'
              element={
                <ProtectedRoute>
                  <InviteCodeManager />
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/users'
              element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/personalization'
              element={
                <ProtectedRoute>
                  <PersonalizationPage />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* Super Admin Routes */}
        {userRole === 'superadmin' && (
          <>
            <Route
              path='/superadmin/companies'
              element={
                <ProtectedRoute>
                  <SuperAdminCompaniesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/superadmin/contact-requests'
              element={
                <ProtectedRoute>
                  <ContactRequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/admin/analytics'
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path='/superadmin/conversations'
              element={
                <ProtectedRoute>
                  <SuperAdminConversationsPage />
                </ProtectedRoute>
              }
            />
          </>
        )}

        <Route
          path='/contribute'
          element={
            <ProtectedRoute>
              <ContributeKnowledgePage />
            </ProtectedRoute>
          }
        />

        <Route
          path='/browse'
          element={
            <ProtectedRoute>
              <KnowledgeBrowsePage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/browse/:itemId?'
          element={
            <ProtectedRoute>
              <KnowledgeBrowsePage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/profile'
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/tutorials'
          element={
            <ProtectedRoute>
              <TutorialsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/tutorials/:tutorialId'
          element={
            <ProtectedRoute>
              <TutorialDetail />
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  )
}

function App () {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <CompanyProvider>
            <Router>
              <AppContent />
            </Router>
          </CompanyProvider>
        </AuthProvider>
        {/* Toast Notifications */}
        <Toaster
          position='top-center'
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#333',
              padding: '16px',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            },
            success: {
              iconTheme: {
                primary: '#4caf50',
                secondary: '#fff'
              }
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff'
              }
            }
          }}
        />
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App
